import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "@/App";

// ── Robust PWA Service Worker Registration ──
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    // updateViaCache: 'none' — forces the browser to always revalidate
    // /sw.js itself from the network (bypassing any HTTP/CDN cache on the
    // script file). Without this, a long-lived client can get permanently
    // stuck controlled by an old service worker because every update
    // check keeps re-fetching a stale cached copy of sw.js that always
    // compares "unchanged", so new releases (and their CACHE_VERSION bump)
    // never get detected even after a normal page reload.
    navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' })
      .then((reg) => {
        console.log('[SW] Registered:', reg.scope);

        // Check for updates immediately
        reg.update().catch(() => {});

        // Listen for new SW installations
        reg.addEventListener('updatefound', () => {
          const newWorker = reg.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // New version available — tell it to activate immediately
                newWorker.postMessage({ type: 'SKIP_WAITING' });
              }
            });
          }
        });

        // Check for SW updates when app comes back to foreground
        document.addEventListener('visibilitychange', () => {
          if (!document.hidden && reg) {
            reg.update().catch(() => {});
          }
        });

        // Also check on online resume (tablet waking from sleep)
        window.addEventListener('online', () => {
          if (reg) reg.update().catch(() => {});
        });

        // Periodic safety-net check for long-lived open tabs that never
        // trigger a visibilitychange/online event (e.g. an unattended
        // kiosk tablet left on the Beverage Master page for hours).
        setInterval(() => { reg.update().catch(() => {}); }, 30 * 60 * 1000);
      })
      .catch((err) => console.warn('[SW] Registration failed:', err));
  });

  // Don't force a reload while the user may be mid-input (counting, typing on
  // the login form, or has any modal/dialog open — e.g. the Beverage Master
  // Add/Edit product dialog). A forced reload here would discard unsaved
  // typed changes; deferring is always safe because the new SW has already
  // activated (clients.claim()'d) and purged old caches by this point — the
  // reload just needs to happen on the user's own next natural navigation.
  const shouldDeferReload = () => {
    const isOnCountPage = window.location.pathname.includes('/count/sessions/') &&
      !window.location.pathname.includes('/sync');
    const isOnLoginPage = window.location.pathname === '/login';
    const hasOpenDialog = !!document.querySelector('[role="dialog"]');
    return isOnCountPage || isOnLoginPage || hasOpenDialog;
  };

  // Listen for SW activation messages — auto-reload to pick up new code
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data?.type === 'SW_ACTIVATED') {
      console.log('[SW] New version activated:', event.data.version);
      if (!shouldDeferReload()) {
        window.location.reload();
      } else {
        // Defer reload — show a subtle notification instead
        window.dispatchEvent(new CustomEvent('sw-updated'));
      }
    }
  });

  // When the controlling SW changes (e.g., after skipWaiting), reload
  let refreshing = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!refreshing) {
      if (shouldDeferReload()) {
        window.dispatchEvent(new CustomEvent('sw-updated'));
        return;
      }
      refreshing = true;
      window.location.reload();
    }
  });
}

// ── Global stale state recovery ──
// If the app loads but React fails to mount (white screen), attempt recovery
window.addEventListener('load', () => {
  setTimeout(() => {
    const root = document.getElementById('root');
    if (root && root.children.length === 0) {
      console.warn('[Recovery] React failed to mount — attempting cache clear');
      if ('caches' in window) {
        caches.keys().then(names => Promise.all(names.map(n => caches.delete(n))))
          .then(() => {
            if ('serviceWorker' in navigator) {
              navigator.serviceWorker.getRegistrations()
                .then(regs => Promise.all(regs.map(r => r.unregister())))
                .then(() => window.location.reload(true))
                .catch(() => window.location.reload(true));
            } else {
              window.location.reload(true);
            }
          })
          .catch(() => window.location.reload(true));
      }
    }
  }, 5000); // Give React 5 seconds to mount
});

// ── Global unhandled error recovery ──
// Catch chunk load failures (stale JS bundles) and auto-recover
window.addEventListener('error', (event) => {
  const msg = event.message || '';
  if (msg.includes('Loading chunk') || msg.includes('Loading CSS chunk') ||
      msg.includes('Unexpected token') || msg.includes('SyntaxError')) {
    console.warn('[Recovery] Chunk load error detected — clearing caches');
    if ('caches' in window) {
      caches.keys().then(names => Promise.all(names.map(n => caches.delete(n))))
        .then(() => window.location.reload(true))
        .catch(() => window.location.reload(true));
    }
  }
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
